import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import { trpc } from "@/lib/trpc";
import { useContextoDaTela } from "@/features/assistente/contexto-de-tela";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  Briefcase,
  Building2,
  User as UserIcon,
  Landmark,
  MapPin,
  Filter,
  CircleDot,
  Target,
  Calendar,
  CheckCircle2,
  XCircle,
  Sparkles,
  Trophy,
  Package,
  X,
  Plus,
  Banknote,
  Bookmark,
  BookmarkPlus,
  Pin,
  Search,
  Trash2,
  Loader2,
  SlidersHorizontal,
  Users as UsersIcon,
  MessageCircle,
  Mail,
  PlusCircle,
  ArrowRightLeft,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import NewDealSheet from "@/components/Crm/NewDealSheet";
import ConfirmLossDialog from "@/components/Crm/ConfirmLossDialog";
import DealsTable from "@/components/Crm/DealsTable";
import TransferDealDialog, { DealTeamRoleBadge } from "@/components/Crm/TransferDealDialog";
import { dealTeamRoleLabel } from "@shared/crm-team";
import { LayoutGrid, Table2 } from "lucide-react";
import { useDealSources } from "@/lib/crm-source";

// ─── Formatação ──────────────────────────────────────────────────────────────
function formatBRL(raw: string | number | null | undefined): string {
  const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
  if (Number.isNaN(n)) return "R$ 0,00";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

const CONTACT_TYPE: Record<
  string,
  { label: string; short: string; icon: any; color: string; bg: string; ring: string }
> = {
  public_admin: {
    label: "Órgão público",
    short: "B2G",
    icon: Landmark,
    color: "text-amber-700 dark:text-amber-300",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    ring: "ring-amber-200 dark:ring-amber-800",
  },
  empresa: {
    label: "Empresa",
    short: "B2B",
    icon: Building2,
    color: "text-sky-700 dark:text-sky-300",
    bg: "bg-sky-50 dark:bg-sky-950/40",
    ring: "ring-sky-200 dark:ring-sky-800",
  },
  cliente_final: {
    label: "Pessoa",
    short: "B2C",
    icon: UserIcon,
    color: "text-emerald-700 dark:text-emerald-300",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    ring: "ring-emerald-200 dark:ring-emerald-800",
  },
};

function initials(name: string | null | undefined, fallback = "?"): string {
  if (!name) return fallback;
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─── Deal Card ───────────────────────────────────────────────────────────────
type KanbanDeal = {
  deal: {
    id: number;
    title: string;
    value: string;
    stageId: number;
    expectedCloseDate: Date | string | null;
  };
  contact: {
    id: number | null;
    name: string | null;
    lastName: string | null;
    companyName: string | null;
    contactType: string | null;
    city: string | null;
    state: string | null;
  } | null;
  owner: { id: number | null; name: string | null } | null;
  products?: Array<{
    productId: number | null;
    name: string | null;
    sku: string | null;
    quantity: number;
  }>;
  team?: Array<{ userId: number; name: string | null; role: string }>;
  lastTransfer?: {
    fromUserId: number | null;
    fromName: string | null;
    toUserId: number;
    toName: string | null;
    fromRole: string | null;
    toRole: string | null;
    reason: string | null;
    createdAt: Date | string;
  } | null;
};

/**
 * Ações que o card dispara mas quem resolve é o quadro — o diálogo de
 * transferência é um só, montado uma vez, e não um por card.
 */
const KanbanActions = createContext<{ onTransfer: ((dealId: number) => void) | null }>({
  onTransfer: null,
});

function DealCard({ item, dragging }: { item: KanbanDeal; dragging?: boolean }) {
  const { onTransfer } = useContext(KanbanActions);
  const typeKey = item.contact?.contactType ?? "empresa";
  const typeCfg = CONTACT_TYPE[typeKey] ?? CONTACT_TYPE.empresa;
  const TypeIcon = typeCfg.icon;
  const fullName = [item.contact?.name, item.contact?.lastName].filter(Boolean).join(" ");
  const displayName = fullName || item.contact?.companyName || "Sem contato";
  // Evita mostrar hash criptografado caso o decrypt falhe (fallback defensivo).
  const rawCompany = item.contact?.companyName ?? null;
  const looksLikeCipher =
    rawCompany != null &&
    rawCompany.length > 40 &&
    /^[a-f0-9]+:[a-f0-9]+:[a-f0-9]+/.test(rawCompany);
  const company = looksLikeCipher ? null : rawCompany;
  const location = [item.contact?.city, item.contact?.state]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={`group relative rounded-lg border bg-card p-3 shadow-sm transition-all cursor-grab active:cursor-grabbing hover:shadow-md hover:border-primary/40 ${
        dragging ? "opacity-60 rotate-2 shadow-xl scale-105" : ""
      }`}
    >
      {/* Type stripe on top */}
      <div className={`absolute inset-x-0 top-0 h-0.5 rounded-t-lg ${typeCfg.bg}`} />

      <div className="flex items-start justify-between gap-2">
        <Link
          href={`/crm/deals/${item.deal.id}`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="text-sm font-semibold leading-tight hover:text-primary line-clamp-2 flex-1"
        >
          {item.deal.title}
        </Link>
        <div
          className={`shrink-0 rounded-md ring-1 ${typeCfg.ring} ${typeCfg.bg} ${typeCfg.color} px-1.5 py-0.5 text-[10px] font-semibold tracking-wide flex items-center gap-1`}
        >
          <TypeIcon className="h-3 w-3" />
          {typeCfg.short}
        </div>
        {onTransfer && !dragging && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Ações da negociação"
                className="shrink-0 -mr-1 rounded-md p-0.5 text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100 hover:bg-muted hover:text-foreground transition-opacity"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onPointerDown={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => onTransfer(item.deal.id)}>
                <ArrowRightLeft className="h-4 w-4 mr-2" />
                Transferir para…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div className="mt-2 text-lg font-bold text-foreground tabular-nums">
        {formatBRL(item.deal.value)}
      </div>

      <div className="mt-2 space-y-0.5">
        <div className="text-xs font-medium text-foreground truncate flex items-center gap-1">
          {fullName ? (
            <UserIcon className="h-3 w-3 text-muted-foreground shrink-0" />
          ) : null}
          <span className="truncate">{displayName}</span>
        </div>
        {company && (
          <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
            {typeKey === "public_admin" ? (
              <Landmark className="h-3 w-3 shrink-0" />
            ) : (
              <Building2 className="h-3 w-3 shrink-0" />
            )}
            <span className="truncate">{company}</span>
          </div>
        )}
        {location && (
          <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        )}
      </div>

      {/* Produtos vinculados ao deal */}
      {item.products && item.products.length > 0 && (
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          <Package className="h-3 w-3 text-muted-foreground shrink-0" />
          {item.products.slice(0, 2).map((p, i) => (
            <span
              key={p.productId ?? i}
              className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground/80 max-w-[140px]"
              title={p.name ?? undefined}
            >
              {p.quantity > 1 && (
                <span className="tabular-nums text-muted-foreground">
                  {p.quantity}×
                </span>
              )}
              <span className="truncate">{p.name ?? "Produto"}</span>
            </span>
          ))}
          {item.products.length > 2 && (
            <span className="text-[10px] font-medium text-muted-foreground">
              +{item.products.length - 2}
            </span>
          )}
        </div>
      )}

      {/* Quem trabalha o lead, por papel. Fica para sempre: o lead que passou
          de SDR para closer continua marcado como tal. */}
      {((item.team?.length ?? 0) > 0 || item.lastTransfer) && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {(item.team ?? []).map((m) => (
            <TooltipProvider key={`${m.userId}-${m.role}`} delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 rounded-full border bg-background pl-0.5 pr-1.5 py-px text-[10px] cursor-default">
                    <DealTeamRoleBadge role={m.role} className="rounded-full px-1 py-0 text-[9px] leading-4" />
                    <span className="font-medium max-w-[72px] truncate">
                      {(m.name ?? "?").split(" ")[0]}
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {m.name ?? "—"} · {dealTeamRoleLabel(m.role)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
          {item.lastTransfer && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1 rounded-full border border-orange-300 bg-orange-50 px-1.5 py-px text-[10px] font-medium text-orange-800 dark:border-orange-900 dark:bg-orange-950 dark:text-orange-300 cursor-default">
                    <ArrowRightLeft className="h-2.5 w-2.5" />
                    {item.lastTransfer.fromName
                      ? `de ${item.lastTransfer.fromName.split(" ")[0]}`
                      : "Transferida"}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>
                    {item.lastTransfer.fromName ?? "Sem responsável"}
                    {item.lastTransfer.fromRole ? ` (${dealTeamRoleLabel(item.lastTransfer.fromRole)})` : ""}
                    {" → "}
                    {item.lastTransfer.toName ?? "—"}
                    {item.lastTransfer.toRole ? ` (${dealTeamRoleLabel(item.lastTransfer.toRole)})` : ""}
                  </p>
                  {item.lastTransfer.reason && (
                    <p className="mt-1 opacity-80">Motivo: {item.lastTransfer.reason}</p>
                  )}
                  <p className="mt-1 opacity-60">
                    {new Date(item.lastTransfer.createdAt).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 pt-2 border-t border-border/60">
        {item.deal.expectedCloseDate ? (
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {new Date(item.deal.expectedCloseDate).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "short",
            })}
          </div>
        ) : (
          <span className="text-[11px] text-muted-foreground">sem prazo</span>
        )}
        {item.owner?.name && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-[10px] font-semibold bg-primary/10 text-primary">
                    {initials(item.owner.name)}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent>{item.owner.name}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}

function DraggableDealCard({ item }: { item: KanbanDeal }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `deal-${item.deal.id}`,
    data: { dealId: item.deal.id, fromStageId: item.deal.stageId },
  });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners}>
      <DealCard item={item} dragging={isDragging} />
    </div>
  );
}

// ─── Kanban Column ───────────────────────────────────────────────────────────
function KanbanColumn({
  stage,
  deals,
  totalValue,
  onCreate,
}: {
  stage: {
    id: number;
    name: string;
    color: string;
    isWon: boolean;
    isLost: boolean;
    probability: number;
  };
  deals: KanbanDeal[];
  totalValue: number;
  onCreate?: (stageId: number) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `stage-${stage.id}`,
    data: { stageId: stage.id, isWon: stage.isWon, isLost: stage.isLost },
  });

  const headerBg = stage.isWon
    ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900"
    : stage.isLost
    ? "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900"
    : "bg-muted/50";

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[300px] max-w-[320px] w-[300px] rounded-xl border bg-background transition ${
        isOver ? "ring-2 ring-primary shadow-lg" : ""
      }`}
    >
      <div className={`px-3 py-2.5 border-b rounded-t-xl ${headerBg}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span
              className="h-2.5 w-2.5 rounded-full shrink-0"
              style={{ backgroundColor: stage.color }}
            />
            <div className="text-sm font-semibold truncate">{stage.name}</div>
            {stage.isWon && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
            {stage.isLost && <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Badge variant="secondary" className="text-[10px] h-5">
              {deals.length}
            </Badge>
            {onCreate && !stage.isWon && !stage.isLost && (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onCreate(stage.id)}
                      className="h-6 w-6 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center shadow-sm hover:shadow transition hover:scale-110"
                      aria-label={`Adicionar oportunidade em ${stage.name}`}
                    >
                      <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Adicionar em {stage.name}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="tabular-nums font-medium text-foreground/80">
            {formatBRL(totalValue)}
          </span>
          {!stage.isWon && !stage.isLost && (
            <span className="flex items-center gap-1">
              <CircleDot className="h-2.5 w-2.5" />
              {stage.probability}%
            </span>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1 h-[calc(100vh-320px)]">
        <div className="p-2 space-y-2">
          {deals.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">
              <div className="h-10 w-10 rounded-full bg-muted mx-auto mb-2 flex items-center justify-center">
                <Target className="h-4 w-4 opacity-40" />
              </div>
              {onCreate && !stage.isWon && !stage.isLost
                ? "Arraste ou clique no + acima"
                : "Arraste uma oportunidade"}
            </div>
          ) : (
            deals.map((d) => <DraggableDealCard key={d.deal.id} item={d} />)
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// ─── Metric Card ─────────────────────────────────────────────────────────────
// ─── Pipeline Selector ───────────────────────────────────────────────────────
function PipelineChips({
  pipelines,
  selectedId,
  onSelect,
}: {
  pipelines: any[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  if (pipelines.length === 0) return null;

  const useSelectOnly = pipelines.length > 3;

  return (
    <>
      {/* Mobile (ou muitos funis): Select compacto */}
      <div className={useSelectOnly ? "block w-full sm:w-auto" : "block sm:hidden w-full"}>
        <Select
          value={selectedId ? String(selectedId) : ""}
          onValueChange={(v) => onSelect(Number(v))}
        >
          <SelectTrigger className="h-9 w-full sm:w-[200px]">
            <SelectValue placeholder="Selecione um funil" />
          </SelectTrigger>
          <SelectContent>
            {pipelines.map((p: any) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.name}
                {p.isDefault && (
                  <span className="text-muted-foreground"> · padrão</span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Desktop (até 3 funis): pílulas */}
      {!useSelectOnly && (
        <div className="hidden sm:flex items-center gap-1 p-1 bg-muted/60 rounded-lg overflow-x-auto max-w-full">
          {pipelines.map((p: any) => (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={`px-3 py-1 text-sm font-medium rounded-md transition whitespace-nowrap ${
                selectedId === p.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.name}
              {p.isDefault && (
                <Sparkles className="h-3 w-3 inline ml-1 opacity-60" />
              )}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Kanban page body ────────────────────────────────────────────────────────
function CrmDealsContent() {
  const pipelinesQuery = trpc.crm.pipelines.list.useQuery({});
  const ensureDefault = trpc.crm.pipelines.ensureDefault.useMutation();
  const [pipelineId, setPipelineId] = useState<number | null>(null);
  const [contactType, setContactType] = useState<string>("all");
  const [ownerUserId, setOwnerUserId] = useState<string>("all");
  const [productId, setProductId] = useState<string>("all");
  const [stageId, setStageId] = useState<string>("all");
  const [source, setSource] = useState<string>("all");
  const [valueMin, setValueMin] = useState<string>("");
  const [valueMax, setValueMax] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [newDealOpen, setNewDealOpen] = useState(false);
  const [newDealStageId, setNewDealStageId] = useState<number | null>(null);
  const [activeViewId, setActiveViewId] = useState<number | null>(null);
  const [saveViewOpen, setSaveViewOpen] = useState(false);

  // IDs de vistas fixadas (aparecem como abas acima da toolbar).
  // Persistido em localStorage — preferência por dispositivo.
  const [pinnedViewIds, setPinnedViewIds] = useState<Set<number>>(() => {
    if (typeof window === "undefined") return new Set();
    try {
      const raw = window.localStorage.getItem("crm-deals-pinned-views");
      if (raw) {
        const arr = JSON.parse(raw) as number[];
        return new Set(arr.filter((n) => typeof n === "number"));
      }
    } catch {
      /* fallback */
    }
    return new Set();
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        "crm-deals-pinned-views",
        JSON.stringify(Array.from(pinnedViewIds)),
      );
    }
  }, [pinnedViewIds]);
  function togglePinView(id: number) {
    setPinnedViewIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Formato de visualização (kanban ou tabela) — persistido em localStorage.
  type ViewMode = "kanban" | "table";
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window === "undefined") return "kanban";
    const saved = window.localStorage.getItem("crm-deals-view");
    return saved === "table" ? "table" : "kanban";
  });
  function changeViewMode(v: ViewMode) {
    setViewMode(v);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("crm-deals-view", v);
    }
  }

  useEffect(() => {
    if (pipelinesQuery.isSuccess && (pipelinesQuery.data?.length ?? 0) === 0) {
      ensureDefault.mutate(undefined, { onSuccess: () => pipelinesQuery.refetch() });
    }
  }, [pipelinesQuery.isSuccess, pipelinesQuery.data?.length]);

  useEffect(() => {
    if (!pipelineId && (pipelinesQuery.data?.length ?? 0) > 0) {
      const def = pipelinesQuery.data!.find((p: any) => p.isDefault) ?? pipelinesQuery.data![0];
      setPipelineId(def.id);
    }
  }, [pipelinesQuery.data, pipelineId]);

  const valueMinNum = valueMin ? Number(valueMin.replace(",", ".")) : undefined;
  const valueMaxNum = valueMax ? Number(valueMax.replace(",", ".")) : undefined;

  // O estado guarda o id da origem como texto. Vistas salvas antigas gravaram a
  // chave do enum antigo ("outbound"), que não é numérica — nesse caso o valor
  // vai como `source` e o servidor faz o De-Para, senão o filtro daquelas
  // vistas passaria a não filtrar nada em silêncio.
  const sourceFilter = useMemo(() => {
    if (source === "all") return {};
    const id = Number(source);
    return Number.isFinite(id) && id > 0 ? { sourceId: id } : { source };
  }, [source]);

  const kanbanQuery = trpc.crm.deals.kanban.useQuery(
    pipelineId
      ? {
          pipelineId,
          contactType: contactType !== "all" ? (contactType as any) : undefined,
          ownerUserId: ownerUserId !== "all" ? Number(ownerUserId) : undefined,
          productId: productId !== "all" ? Number(productId) : undefined,
          ...sourceFilter,
          valueMin: Number.isFinite(valueMinNum) ? valueMinNum : undefined,
          valueMax: Number.isFinite(valueMaxNum) ? valueMaxNum : undefined,
        }
      : (undefined as any),
    { enabled: !!pipelineId },
  );

  // Carrega vendedores (para filtro) — só se o usuário pode ver todos.
  const { can } = usePermissions();
  const podeTransferir = can("crm:deals_assign") || can("crm:deals_assign_all");
  const [transferDealId, setTransferDealId] = useState<number | null>(null);
  const canViewAll = can("crm:deals_view_all");
  const canShareViews = can("crm:views_share");
  const sellersQuery = trpc.crm.sellers.list.useQuery({}, { enabled: canViewAll });
  const productsQuery = trpc.crm.products.list.useQuery({ limit: 200 });
  const viewsQuery = trpc.crm.views.list.useQuery();

  // Query adicional para o modo tabela — busca em formato list (com paginação).
  const listQuery = trpc.crm.deals.list.useQuery(
    pipelineId
      ? {
          pipelineId,
          contactType: contactType !== "all" ? (contactType as any) : undefined,
          ownerUserId: ownerUserId !== "all" ? Number(ownerUserId) : undefined,
          productId: productId !== "all" ? Number(productId) : undefined,
          stageId: stageId !== "all" ? Number(stageId) : undefined,
          ...sourceFilter,
          valueMin: Number.isFinite(valueMinNum) ? valueMinNum : undefined,
          valueMax: Number.isFinite(valueMaxNum) ? valueMaxNum : undefined,
          limit: 500,
        }
      : (undefined as any),
    { enabled: !!pipelineId && viewMode === "table" },
  );

  // Helpers de filtros.
  const activeFilterCount =
    (contactType !== "all" ? 1 : 0) +
    (ownerUserId !== "all" ? 1 : 0) +
    (productId !== "all" ? 1 : 0) +
    (stageId !== "all" ? 1 : 0) +
    (source !== "all" ? 1 : 0) +
    (valueMin ? 1 : 0) +
    (valueMax ? 1 : 0);

  const currentFilters = useMemo(
    () => ({
      contactType: contactType !== "all" ? contactType : undefined,
      ownerUserId: ownerUserId !== "all" ? Number(ownerUserId) : undefined,
      productId: productId !== "all" ? Number(productId) : undefined,
      stageId: stageId !== "all" ? Number(stageId) : undefined,
      source: source !== "all" ? source : undefined,
      valueMin: valueMin || undefined,
      valueMax: valueMax || undefined,
    }),
    [contactType, ownerUserId, productId, stageId, source, valueMin, valueMax],
  );

  function countFiltersInView(v: any): number {
    const f = (v?.filters ?? {}) as Record<string, any>;
    let n = 0;
    if (f.contactType && f.contactType !== "all") n++;
    if (f.ownerUserId != null) n++;
    if (f.productId != null) n++;
    if (f.stageId != null) n++;
    if (f.source && f.source !== "all") n++;
    if (f.valueMin) n++;
    if (f.valueMax) n++;
    return n;
  }

  function clearAllFilters() {
    setContactType("all");
    setOwnerUserId("all");
    setProductId("all");
    setStageId("all");
    setSource("all");
    setValueMin("");
    setValueMax("");
    setActiveViewId(null);
  }

  function applyView(v: any) {
    const f = (v?.filters ?? {}) as Record<string, any>;
    setContactType(f.contactType ?? "all");
    setOwnerUserId(f.ownerUserId != null ? String(f.ownerUserId) : "all");
    setProductId(f.productId != null ? String(f.productId) : "all");
    setStageId(f.stageId != null ? String(f.stageId) : "all");
    setSource(f.source ?? "all");
    setValueMin(f.valueMin != null ? String(f.valueMin) : "");
    setValueMax(f.valueMax != null ? String(f.valueMax) : "");
    setActiveViewId(v?.id ?? null);
  }

  const utils = trpc.useUtils();
  const changeStage = trpc.crm.deals.changeStage.useMutation({
    onSuccess: () => {
      utils.crm.deals.kanban.invalidate();
      toast.success("Oportunidade atualizada");
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [activeDeal, setActiveDeal] = useState<KanbanDeal | null>(null);
  const [lossPicker, setLossPicker] = useState<{
    dealId: number;
    dealTitle: string;
    toStageId: number;
  } | null>(null);

  function handleDragStart(e: DragStartEvent) {
    const dealId = (e.active.data.current as any)?.dealId;
    if (!dealId) return;
    const stages = kanbanQuery.data?.stages ?? [];
    const deals = kanbanQuery.data?.dealsByStage ?? {};
    for (const s of stages) {
      const found = (deals as any)[s.id]?.find((d: KanbanDeal) => d.deal.id === dealId);
      if (found) {
        setActiveDeal(found);
        return;
      }
    }
  }

  function handleDragEnd(e: DragEndEvent) {
    setActiveDeal(null);
    if (!e.over) return;
    const { dealId, fromStageId } = (e.active.data.current as any) ?? {};
    const { stageId, isLost } = (e.over.data.current as any) ?? {};
    if (!dealId || !stageId || stageId === fromStageId) return;
    if (isLost) {
      const draggedTitle =
        (kanbanQuery.data?.dealsByStage as any)?.[fromStageId]?.find(
          (d: KanbanDeal) => d.deal.id === dealId,
        )?.deal.title ?? "Oportunidade";
      setLossPicker({ dealId, dealTitle: draggedTitle, toStageId: stageId });
      return;
    }
    changeStage.mutate({ id: dealId, toStageId: stageId });
  }

  const stages = kanbanQuery.data?.stages ?? [];
  const rawDealsByStage = (kanbanQuery.data?.dealsByStage ?? {}) as Record<
    number,
    KanbanDeal[]
  >;

  // Filtro client-side por termo de busca (título do deal, nome do contato, empresa).
  const searchTerm = search.trim().toLocaleLowerCase();
  function matchesSearch(item: {
    deal: { title: string };
    contact?: {
      name?: string | null;
      lastName?: string | null;
      companyName?: string | null;
      phoneNumber?: string | null;
    } | null;
  }): boolean {
    if (!searchTerm) return true;
    const haystack = [
      item.deal.title,
      item.contact?.name,
      item.contact?.lastName,
      item.contact?.companyName,
      item.contact?.phoneNumber,
    ]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase();
    return haystack.includes(searchTerm);
  }

  const dealsByStage = useMemo(() => {
    if (!searchTerm) return rawDealsByStage;
    const out: Record<number, KanbanDeal[]> = {};
    for (const key of Object.keys(rawDealsByStage)) {
      const id = Number(key);
      out[id] = rawDealsByStage[id].filter((d) => matchesSearch(d));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawDealsByStage, searchTerm]);

  const filteredListRows = useMemo(() => {
    const rows = (listQuery.data?.rows ?? []) as any[];
    if (!searchTerm) return rows;
    return rows.filter((r) => matchesSearch(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQuery.data, searchTerm]);

  const totalsByStage = useMemo(() => {
    const totals: Record<number, number> = {};
    for (const s of stages) {
      const list = dealsByStage[s.id] ?? [];
      totals[s.id] = list.reduce((sum, d) => sum + Number(d.deal.value ?? 0), 0);
    }
    return totals;
  }, [stages, dealsByStage]);

  /**
   * O funil que está na tela: etapas, total por etapa e uma amostra dos
   * negócios. É o que permite "mova a oportunidade da Acme para Proposta"
   * sem a pessoa precisar dizer ids.
   */
  useContextoDaTela({
    titulo: `Funil de vendas: ${pipelinesQuery.data?.find((p: any) => p.id === pipelineId)?.name ?? "—"}`,
    dados: {
      pipelineId,
      visao: viewMode,
      busca: searchTerm || null,
      etapas: stages.map((e: any) => ({
        id: e.id,
        nome: e.name,
        negocios: (dealsByStage[e.id] ?? []).length,
        valorTotal: totalsByStage[e.id] ?? 0,
        amostra: (dealsByStage[e.id] ?? []).slice(0, 5).map((d: any) => ({
          id: d.deal.id,
          titulo: d.deal.title,
          valor: d.deal.value,
          contato: d.contact?.name ?? null,
        })),
      })),
    },
  });

  const typeFilters = [
    { key: "all", label: "Todos", short: "Todos", cfg: null as any },
    { key: "public_admin", label: "Órgão", short: "B2G", cfg: CONTACT_TYPE.public_admin },
    { key: "empresa", label: "Empresa", short: "B2B", cfg: CONTACT_TYPE.empresa },
    { key: "cliente_final", label: "Pessoa", short: "B2C", cfg: CONTACT_TYPE.cliente_final },
  ];

  return (
    <div className="space-y-5">
      {/* ── Page header ──────────────────────────────── */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Briefcase className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">
              Oportunidades
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground hidden sm:block">
              Arraste entre colunas para atualizar o estágio.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-0 lg:flex-initial">
            <PipelineChips
              pipelines={pipelinesQuery.data ?? []}
              selectedId={pipelineId}
              onSelect={setPipelineId}
            />
          </div>
          {/* Toggle de visualização Kanban | Tabela */}
          <div className="inline-flex items-center gap-0.5 p-0.5 bg-muted/60 rounded-md shrink-0">
            <button
              type="button"
              onClick={() => changeViewMode("kanban")}
              className={`p-1.5 rounded transition ${
                viewMode === "kanban"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Visualizar como Kanban"
              aria-label="Kanban"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => changeViewMode("table")}
              className={`p-1.5 rounded transition ${
                viewMode === "table"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Visualizar como tabela"
              aria-label="Tabela"
            >
              <Table2 className="h-4 w-4" />
            </button>
          </div>
          <Button
            className="shrink-0"
            onClick={() => setNewDealOpen(true)}
            disabled={!pipelineId || stages.length === 0}
          >
            <Target className="h-4 w-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Nova oportunidade</span>
            <span className="sm:hidden ml-1.5">Nova</span>
          </Button>
        </div>
      </div>

      {/* ── Abas de vistas fixadas ──────────────────── */}
      {(() => {
        const pinnedViews = (viewsQuery.data ?? []).filter((v: any) =>
          pinnedViewIds.has(v.id),
        );
        if (pinnedViews.length === 0) return null;
        return (
          <div className="flex items-center gap-1 flex-wrap border-b -mb-px">
            <button
              onClick={clearAllFilters}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md border border-b-0 transition ${
                activeViewId === null
                  ? "bg-background border-border text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40"
              }`}
            >
              <Briefcase className="h-3.5 w-3.5" />
              Todas
            </button>
            {pinnedViews.map((v: any) => {
              const count = countFiltersInView(v);
              const isActive = activeViewId === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => applyView(v)}
                  className={`group inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md border border-b-0 transition max-w-[200px] ${
                    isActive
                      ? "bg-background border-border text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40"
                  }`}
                >
                  <Bookmark
                    className={`h-3 w-3 shrink-0 ${
                      isActive ? "fill-primary text-primary" : ""
                    }`}
                  />
                  <span className="truncate">{v.name}</span>
                  {count > 0 && (
                    <Badge
                      variant="secondary"
                      className="h-4 px-1.5 text-[10px] tabular-nums"
                    >
                      {count}
                    </Badge>
                  )}
                  <span
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePinView(v.id);
                    }}
                    title="Desafixar"
                    className="opacity-0 group-hover:opacity-100 -mr-1 ml-0.5 rounded p-0.5 hover:bg-muted-foreground/20"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* ── Filters bar (tudo em uma linha) ─────────── */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
          {/* Barra de pesquisa */}
          <div className="relative w-full sm:w-[260px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pesquisar por título, contato, empresa…"
              className="h-8 pl-8 pr-8 text-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded hover:bg-muted flex items-center justify-center text-muted-foreground"
                aria-label="Limpar busca"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <FiltersPopover
            contactType={contactType}
            ownerUserId={ownerUserId}
            productId={productId}
            stageId={stageId}
            source={source}
            valueMin={valueMin}
            valueMax={valueMax}
            activeCount={activeFilterCount}
            canViewAll={canViewAll}
            sellers={sellersQuery.data ?? []}
            products={(productsQuery.data?.rows ?? []) as any[]}
            stages={stages as any}
            onContactType={setContactType}
            onOwnerUserId={setOwnerUserId}
            onProductId={setProductId}
            onStageId={setStageId}
            onSource={setSource}
            onValueMin={setValueMin}
            onValueMax={setValueMax}
            onClear={clearAllFilters}
          />

          <ViewsDropdown
            views={viewsQuery.data ?? []}
            activeViewId={activeViewId}
            activeCount={activeFilterCount}
            pinnedIds={pinnedViewIds}
            onApply={applyView}
            onSaveNew={() => setSaveViewOpen(true)}
            onCleared={() => setActiveViewId(null)}
            onTogglePin={togglePinView}
          />

          {activeFilterCount > 0 && (
            <ActiveFilterChips
              contactType={contactType}
              ownerUserId={ownerUserId}
              productId={productId}
              stageId={stageId}
              source={source}
              valueMin={valueMin}
              valueMax={valueMax}
              sellers={sellersQuery.data ?? []}
              products={(productsQuery.data?.rows ?? []) as any[]}
              stages={stages as any}
              onContactType={setContactType}
              onOwnerUserId={setOwnerUserId}
              onProductId={setProductId}
              onStageId={setStageId}
              onSource={setSource}
              onValueMin={setValueMin}
              onValueMax={setValueMax}
            />
          )}
        </div>

        {activeFilterCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs shrink-0"
            onClick={clearAllFilters}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      {/* ── Visualização ─────────────────────────────── */}
      {viewMode === "table" ? (
        <DealsTable
          rows={filteredListRows as any}
          isLoading={listQuery.isLoading}
          // Alimenta a célula de estágio editável. Já está carregado: o
          // kanbanQuery roda nos dois modos de visualização.
          stages={stages as any}
        />
      ) : kanbanQuery.isLoading ? (
        <div className="flex gap-3 overflow-x-auto">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col min-w-[300px] w-[300px] rounded-xl border bg-background"
            >
              <div className="px-3 py-2.5 border-b bg-muted/50">
                <Skeleton className="h-4 w-24 mb-1" />
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="p-2 space-y-2">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : stages.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Target className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <h3 className="font-semibold mb-1">Nenhum estágio configurado</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Crie um funil em Configurações → CRM para começar.
            </p>
          </CardContent>
        </Card>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <KanbanActions.Provider
            value={{ onTransfer: podeTransferir ? setTransferDealId : null }}
          >
          <ScrollArea className="w-full pb-3">
            <div className="flex gap-3 pb-3">
              {stages.map((s: any) => (
                <KanbanColumn
                  key={s.id}
                  stage={s}
                  deals={dealsByStage[s.id] ?? []}
                  totalValue={totalsByStage[s.id] ?? 0}
                  onCreate={(stageId) => {
                    setNewDealStageId(stageId);
                    setNewDealOpen(true);
                  }}
                />
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
          <DragOverlay>{activeDeal ? <DealCard item={activeDeal} dragging /> : null}</DragOverlay>
          </KanbanActions.Provider>
        </DndContext>
      )}

      {/* ── Dialogs ─────────────────────────────────── */}
      {transferDealId != null && (
        <TransferDealDialog
          dealId={transferDealId}
          open
          onClose={() => setTransferDealId(null)}
        />
      )}
      {pipelineId && (
        <NewDealSheet
          open={newDealOpen}
          onOpenChange={(v) => {
            setNewDealOpen(v);
            if (!v) setNewDealStageId(null);
          }}
          pipelineId={pipelineId}
          stages={stages as any}
          initialStageId={newDealStageId}
          onCreated={() => {
            kanbanQuery.refetch();
          }}
        />
      )}
      {pipelineId && (
        <SaveViewDialog
          open={saveViewOpen}
          onOpenChange={setSaveViewOpen}
          filters={currentFilters}
          canShare={canShareViews}
          onSaved={(id) => setActiveViewId(id)}
        />
      )}
      <ConfirmLossDialog
        open={!!lossPicker}
        dealTitle={lossPicker?.dealTitle ?? ""}
        onClose={() => setLossPicker(null)}
        onConfirm={(lossReasonId) => {
          if (!lossPicker) return;
          changeStage.mutate({
            id: lossPicker.dealId,
            toStageId: lossPicker.toStageId,
            lossReasonId,
          });
          setLossPicker(null);
        }}
      />
    </div>
  );
}

// ─── Popover de filtros ──────────────────────────────────────────────────────
function FiltersPopover({
  contactType,
  ownerUserId,
  productId,
  stageId,
  source,
  valueMin,
  valueMax,
  activeCount,
  canViewAll,
  sellers,
  products,
  stages,
  onContactType,
  onOwnerUserId,
  onProductId,
  onStageId,
  onSource,
  onValueMin,
  onValueMax,
  onClear,
}: {
  contactType: string;
  ownerUserId: string;
  productId: string;
  stageId: string;
  source: string;
  valueMin: string;
  valueMax: string;
  activeCount: number;
  canViewAll: boolean;
  sellers: any[];
  products: any[];
  stages: Array<{ id: number; name: string; color: string; isWon: boolean; isLost: boolean }>;
  onContactType: (v: string) => void;
  onOwnerUserId: (v: string) => void;
  onProductId: (v: string) => void;
  onStageId: (v: string) => void;
  onSource: (v: string) => void;
  onValueMin: (v: string) => void;
  onValueMax: (v: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const sources = useDealSources();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={activeCount > 0 ? "default" : "outline"}
          className="h-8"
        >
          <SlidersHorizontal className="h-3.5 w-3.5 mr-1.5" />
          Filtros
          {activeCount > 0 && (
            <Badge
              variant="secondary"
              className="ml-1.5 h-4 px-1.5 text-[10px] bg-background/20 text-background"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h4 className="text-sm font-semibold flex items-center gap-2">
            <Filter className="h-3.5 w-3.5" />
            Filtros
            {activeCount > 0 && (
              <Badge variant="secondary" className="h-5 text-[10px]">
                {activeCount} ativo{activeCount > 1 ? "s" : ""}
              </Badge>
            )}
          </h4>
          {activeCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={onClear}
            >
              Limpar tudo
            </Button>
          )}
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-4 space-y-4">
          {/* Tipo de contato */}
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Tipo de contato
            </Label>
            <div className="grid grid-cols-2 gap-1">
              {[
                { key: "all", label: "Todos", cfg: null as any },
                {
                  key: "public_admin",
                  label: "B2G · Órgão",
                  cfg: CONTACT_TYPE.public_admin,
                },
                {
                  key: "empresa",
                  label: "B2B · Empresa",
                  cfg: CONTACT_TYPE.empresa,
                },
                {
                  key: "cliente_final",
                  label: "B2C · Pessoa",
                  cfg: CONTACT_TYPE.cliente_final,
                },
              ].map((f) => {
                const active = contactType === f.key;
                const Icon = f.cfg?.icon;
                return (
                  <button
                    key={f.key}
                    onClick={() => onContactType(f.key)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border transition ${
                      active
                        ? f.cfg
                          ? `${f.cfg.bg} ${f.cfg.color} ${f.cfg.ring} ring-1 border-transparent font-medium`
                          : "bg-foreground text-background border-transparent font-medium"
                        : "hover:bg-muted/40"
                    }`}
                  >
                    {Icon && <Icon className="h-3 w-3" />}
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Separator />

          {/* Vendedor */}
          {canViewAll && (
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Trophy className="h-3 w-3" />
                Vendedor
              </Label>
              <Select value={ownerUserId} onValueChange={onOwnerUserId}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os vendedores</SelectItem>
                  {sellers.map((s: any) => (
                    <SelectItem key={s.profile.userId} value={String(s.profile.userId)}>
                      {s.user.name ?? s.user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Produto */}
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Package className="h-3 w-3" />
              Produto
            </Label>
            <Select value={productId} onValueChange={onProductId}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os produtos</SelectItem>
                {products.map((row: any) => (
                  <SelectItem key={row.product.id} value={String(row.product.id)}>
                    {row.product.name}
                    {row.product.sku && (
                      <span className="text-muted-foreground text-[10px] ml-1">
                        · {row.product.sku}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Estágio */}
          {stages.length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Target className="h-3 w-3" />
                Estágio
              </Label>
              <Select value={stageId} onValueChange={onStageId}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os estágios</SelectItem>
                  {stages.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full shrink-0"
                          style={{ backgroundColor: s.color }}
                        />
                        {s.name}
                        {s.isWon && (
                          <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                        )}
                        {s.isLost && (
                          <XCircle className="h-3 w-3 text-red-600" />
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Origem */}
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <CircleDot className="h-3 w-3" />
              Origem do lead
            </Label>
            <Select value={source} onValueChange={onSource}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as origens</SelectItem>
                {sources.options.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    <span className="flex items-center gap-2">
                      <CircleDot className="h-3.5 w-3.5 text-muted-foreground" />
                      {s.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Faixa de valor */}
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Banknote className="h-3 w-3" />
              Faixa de valor
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Mínimo</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="R$ 0,00"
                  value={valueMin}
                  onChange={(e) => onValueMin(e.target.value)}
                  className="h-8 text-sm tabular-nums mt-0.5"
                />
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Máximo</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="sem limite"
                  value={valueMax}
                  onChange={(e) => onValueMax(e.target.value)}
                  className="h-8 text-sm tabular-nums mt-0.5"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t px-4 py-2 flex items-center justify-end bg-muted/30">
          <Button size="sm" onClick={() => setOpen(false)}>
            Aplicar
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Dropdown de vistas salvas ───────────────────────────────────────────────
function ViewsDropdown({
  views,
  activeViewId,
  activeCount,
  pinnedIds,
  onApply,
  onSaveNew,
  onCleared,
  onTogglePin,
}: {
  views: any[];
  activeViewId: number | null;
  activeCount: number;
  pinnedIds: Set<number>;
  onApply: (v: any) => void;
  onSaveNew: () => void;
  onCleared: () => void;
  onTogglePin: (id: number) => void;
}) {
  const utils = trpc.useUtils();
  const del = trpc.crm.views.delete.useMutation({
    onSuccess: () => {
      toast.success("Vista removida");
      utils.crm.views.list.invalidate();
      onCleared();
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const active = views.find((v) => v.id === activeViewId);
  // Se a vista ativa já está fixada como aba acima, o label se torna redundante.
  const activeIsPinned = active ? pinnedIds.has(active.id) : false;
  const showActiveName = !!active && !activeIsPinned;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant={showActiveName ? "default" : "outline"}
          className="h-8"
        >
          <Bookmark className="h-3.5 w-3.5 mr-1.5" />
          {showActiveName ? active!.name : "Vistas"}
          {views.length > 0 && !showActiveName && (
            <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[10px]">
              {views.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[260px]">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide">
          Minhas vistas
        </DropdownMenuLabel>
        {views.length === 0 ? (
          <div className="px-2 py-3 text-xs text-muted-foreground text-center">
            Nenhuma vista salva ainda.
          </div>
        ) : (
          views.map((v: any) => {
            const isPinned = pinnedIds.has(v.id);
            return (
              <div
                key={v.id}
                className={`group flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted/60 cursor-pointer ${
                  activeViewId === v.id ? "bg-primary/10" : ""
                }`}
                onClick={() => onApply(v)}
              >
                <Bookmark
                  className={`h-3.5 w-3.5 shrink-0 ${
                    activeViewId === v.id
                      ? "fill-primary text-primary"
                      : "text-muted-foreground"
                  }`}
                />
                <span className="text-sm flex-1 min-w-0 truncate">{v.name}</span>
                {v.isShared && (
                  <UsersIcon className="h-3 w-3 text-muted-foreground" />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTogglePin(v.id);
                  }}
                  title={isPinned ? "Desafixar" : "Fixar como aba"}
                  className={`${
                    isPinned
                      ? "text-primary"
                      : "opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {isPinned ? (
                    <Pin className="h-3 w-3 fill-primary" />
                  ) : (
                    <Pin className="h-3 w-3" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm(`Remover vista "${v.name}"?`)) {
                      del.mutate({ id: v.id });
                    }
                  }}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            );
          })
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            onSaveNew();
          }}
          disabled={activeCount === 0}
          className={activeCount === 0 ? "opacity-50" : ""}
        >
          <BookmarkPlus className="h-3.5 w-3.5 mr-2" />
          {activeCount === 0
            ? "Aplique filtros para salvar"
            : "Salvar filtros atuais como vista"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Chips dos filtros ativos ────────────────────────────────────────────────
function ActiveFilterChips({
  contactType,
  ownerUserId,
  productId,
  stageId,
  source,
  valueMin,
  valueMax,
  sellers,
  products,
  stages,
  onContactType,
  onOwnerUserId,
  onProductId,
  onStageId,
  onSource,
  onValueMin,
  onValueMax,
}: {
  contactType: string;
  ownerUserId: string;
  productId: string;
  stageId: string;
  source: string;
  valueMin: string;
  valueMax: string;
  sellers: any[];
  products: any[];
  stages: Array<{ id: number; name: string; color: string }>;
  onContactType: (v: string) => void;
  onOwnerUserId: (v: string) => void;
  onProductId: (v: string) => void;
  onStageId: (v: string) => void;
  onSource: (v: string) => void;
  onValueMin: (v: string) => void;
  onValueMax: (v: string) => void;
}) {
  const sources = useDealSources();
  const chips: Array<{ key: string; label: string; onRemove: () => void }> = [];
  if (contactType !== "all") {
    const cfg = CONTACT_TYPE[contactType];
    chips.push({
      key: "type",
      label: cfg?.label ?? contactType,
      onRemove: () => onContactType("all"),
    });
  }
  if (ownerUserId !== "all") {
    const s = sellers.find((x: any) => String(x.profile.userId) === ownerUserId);
    chips.push({
      key: "owner",
      label: s?.user?.name ?? `Vendedor #${ownerUserId}`,
      onRemove: () => onOwnerUserId("all"),
    });
  }
  if (productId !== "all") {
    const p = products.find((x: any) => String(x.product.id) === productId);
    chips.push({
      key: "product",
      label: p?.product?.name ?? `Produto #${productId}`,
      onRemove: () => onProductId("all"),
    });
  }
  if (stageId !== "all") {
    const st = stages.find((x) => String(x.id) === stageId);
    chips.push({
      key: "stage",
      label: st ? `Estágio: ${st.name}` : `Estágio #${stageId}`,
      onRemove: () => onStageId("all"),
    });
  }
  if (source !== "all") {
    chips.push({
      key: "source",
      // Vista salva antiga guarda a chave do enum antigo, que não é numérica —
      // aí o `label` não acha e o próprio valor serve de rótulo.
      label: sources.label(Number(source)) ?? source,
      onRemove: () => onSource("all"),
    });
  }
  if (valueMin) {
    chips.push({
      key: "valueMin",
      label: `≥ ${Number(valueMin).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })}`,
      onRemove: () => onValueMin(""),
    });
  }
  if (valueMax) {
    chips.push({
      key: "valueMax",
      label: `≤ ${Number(valueMax).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      })}`,
      onRemove: () => onValueMax(""),
    });
  }
  if (chips.length === 0) return null;
  return (
    <>
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/30"
        >
          {c.label}
          <button
            onClick={c.onRemove}
            className="h-4 w-4 rounded-full hover:bg-primary/20 flex items-center justify-center"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </>
  );
}

// ─── Salvar vista ────────────────────────────────────────────────────────────
function SaveViewDialog({
  open,
  onOpenChange,
  filters,
  canShare,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  filters: Record<string, any>;
  canShare: boolean;
  onSaved?: (id: number) => void;
}) {
  const [name, setName] = useState("");
  const [isShared, setIsShared] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setIsShared(false);
    }
  }, [open]);

  const utils = trpc.useUtils();
  const create = trpc.crm.views.create.useMutation({
    onSuccess: (v: any) => {
      toast.success("Vista salva");
      utils.crm.views.list.invalidate();
      onOpenChange(false);
      if (v?.id) onSaved?.(v.id);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4" />
            Salvar vista
          </DialogTitle>
          <DialogDescription>
            Dê um nome para voltar rapidamente a este conjunto de filtros.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-xs">Nome</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: B2G São Paulo — Q4"
              className="mt-1"
              autoFocus
            />
          </div>
          {canShare && (
            <label className="flex items-start gap-3 cursor-pointer rounded-md border p-3 hover:bg-muted/40">
              <Switch
                checked={isShared}
                onCheckedChange={setIsShared}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium flex items-center gap-1.5">
                  <UsersIcon className="h-3.5 w-3.5" />
                  Compartilhar com a equipe
                </span>
                <span className="text-xs text-muted-foreground mt-0.5 block">
                  Todos os usuários do CRM verão esta vista no dropdown.
                </span>
              </span>
            </label>
          )}
          <div className="rounded-md bg-muted/40 border p-2.5 text-xs text-muted-foreground">
            <div className="font-medium mb-1">Filtros incluídos:</div>
            <ul className="space-y-0.5">
              {Object.entries(filters)
                .filter(([, v]) => v !== undefined && v !== "" && v !== null)
                .map(([k, v]) => (
                  <li key={k}>
                    <span className="text-foreground/70">{k}:</span> {String(v)}
                  </li>
                ))}
              {Object.values(filters).every(
                (v) => v === undefined || v === "" || v === null,
              ) && <li>Nenhum filtro ativo.</li>}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              if (!name.trim()) return toast.error("Nome é obrigatório");
              create.mutate({
                name: name.trim(),
                filters,
                isShared,
              } as any);
            }}
            disabled={create.isPending}
          >
            {create.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Salvar vista
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CrmDeals() {
  return (
    <DashboardLayout>
      <PermissionGuard permission="crm:deals_view">
        <CrmDealsContent />
      </PermissionGuard>
    </DashboardLayout>
  );
}
